import React from "react";
import { useTranslation } from "@repo/core";
import type { LegalBlock, LegalDocument } from "../../content/legal/types";

interface LegalDocumentViewProps {
  /** Named `legalDocument` rather than `document` to avoid shadowing the DOM global. */
  legalDocument: LegalDocument;
}

/** Anchor target for a section so the table of contents can link to it. */
const sectionId = (index: number) => `section-${index + 1}`;

const LegalBlockView: React.FC<{ block: LegalBlock }> = ({ block }) => {
  switch (block.kind) {
    case "clause":
      return (
        <p className="flex gap-3 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
          <span className="w-12 shrink-0 font-medium text-gray-500 dark:text-gray-400">
            {block.number}
          </span>
          <span>{block.text}</span>
        </p>
      );
    case "bullets":
      return (
        <ul className="ml-12 list-disc space-y-1.5 pl-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
          {block.items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      );
    case "note":
      return (
        <div className="ml-12 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
          {block.lines.map((line, index) => (
            <p key={index}>{line}</p>
          ))}
        </div>
      );
  }
};

const LegalDocumentView: React.FC<LegalDocumentViewProps> = ({ legalDocument }) => {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <article className="rounded-lg bg-white p-6 shadow sm:p-10 dark:bg-gray-800">
        <header className="border-b border-gray-200 pb-6 dark:border-gray-700">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl dark:text-white">
            {legalDocument.title}
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{legalDocument.effectiveFrom}</p>
        </header>

        <nav aria-label={t("legal.contents")} className="border-b border-gray-200 py-6 dark:border-gray-700">
          <h2 className="mb-3 text-xs font-semibold tracking-wide text-gray-500 uppercase dark:text-gray-400">
            {t("legal.contents")}
          </h2>
          <ol className="grid gap-1.5 sm:grid-cols-2">
            {legalDocument.sections.map((section, index) => (
              <li key={sectionId(index)}>
                <a
                  href={`#${sectionId(index)}`}
                  className="text-sm text-blue-600 hover:underline dark:text-blue-400"
                >
                  {section.heading}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-8 pt-8">
          {legalDocument.sections.map((section, index) => (
            <section key={sectionId(index)} id={sectionId(index)} className="scroll-mt-24">
              <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
                {section.heading}
              </h2>
              <div className="space-y-3">
                {section.blocks.map((block, blockIndex) => (
                  <LegalBlockView key={blockIndex} block={block} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </article>
    </div>
  );
};

export default LegalDocumentView;
